import { memo, useEffect, useRef, useState, useCallback } from "react";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import moment from "moment";
import _, { cloneDeep } from "lodash";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import styled from "@emotion/styled";

import useChartWidth from "hooks/useChartWidth";
import ChartBox from "../WidgetBox";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { DEFAULT_OPTION } from "constant";
import {
    transformSensorMessage,
    calculateIntensityAtPosition,
} from "utils/heatmap";
import dashboardService from "service/dashboardService";
import { getRandomString, isHttpSuccess } from "utils/functions";
import Layout1 from "assets/svg/heatmap-layout/layout1.svg";
import { fileToUri } from "utils/file";
import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
import HeatmapQuery from "service/heatmapQuery";

const WS = new HeatmapQuery();

const calculateDimensions = (
    maxWidth: number,
    maxHeight: number,
    ratio: number
) => {
    let finalWidth = maxWidth;
    let finalHeight = maxHeight;

    if (maxWidth / maxHeight > ratio) {
        // Width is greater than height
        finalWidth = maxHeight * ratio;
    } else {
        // Height is greater than width
        finalHeight = maxWidth / ratio;
    }

    return {
        width: finalWidth,
        height: finalHeight,
    };
};

const appendIntensityToHumanData = (
    humanData: Map<string, number>,
    maxWidth: number,
    x: number,
    y: number,
    sigma: number,
    i: number
) => {
    const halfSize = Math.floor(maxWidth / 2);
    for (let j = 0; j < maxWidth; j++) {
        for (let k = 0; k < maxWidth; k++) {
            const posX = x - halfSize + k;
            const posY = y - halfSize + j;

            let intensity = calculateIntensityAtPosition(
                maxWidth,
                sigma,
                x,
                y,
                posX,
                posY,
                i
            );

            if (intensity) {
                const key = `${posX},${posY}`;
                if (humanData.has(key)) {
                    const combinedIntensity =
                        (humanData.get(key) ?? 0) + intensity;
                    humanData.set(key, combinedIntensity);
                } else {
                    humanData.set(key, intensity);
                }
            }
        }
    }
};

const SensorInfoWrapper = styled.div`
    position: absolute;
    top: 50px;
    right: 20px;
    z-index: 1;
    font-size: 14px;
`;

const SensorInfo = ({ sensorNames }: any) => {
    const renderSensorTooltip = (props: any) => (
        <Tooltip className="tooltip-heatmap-sensors" {...props}>
            <div>
                <div className="tooltip-heatmap-sensors-title">Sensors:</div>
                {sensorNames?.map((sensorName: string, index: number) => {
                    return (
                        <div key={sensorName + String(index)}>
                            <span>Sensor {index + 1}: </span>
                            <span style={{ color: "#00CBAF" }}>
                                {sensorName}
                            </span>
                        </div>
                    );
                })}
            </div>
        </Tooltip>
    );

    return (
        <SensorInfoWrapper>
            <span>Sensors: </span>
            <OverlayTrigger
                placement="bottom"
                delay={{ show: 100, hide: 100 }}
                overlay={renderSensorTooltip}
            >
                <span style={{ color: "#00CBAF" }}>
                    {sensorNames.length} sensors
                </span>
            </OverlayTrigger>
        </SensorInfoWrapper>
    );
};

const SingleSensorInfoWrapper = styled.div`
    position: absolute;
    top: 50px;
    right: 20px;
    z-index: 1;
    font-size: 14px;
`;

const SingleSensorInfo = ({ sensorName }: any) => {
    return (
        <SingleSensorInfoWrapper>
            {sensorName ? (
                <>
                    <span>Sensor: </span>
                    <span style={{ color: "#00CBAF" }}>{sensorName}</span>
                </>
            ) : (
                ""
            )}
        </SingleSensorInfoWrapper>
    );
};

const LayoutWrapper = styled.div`
    position: absolute;
    top: 75px;
    right: 20px;
    z-index: 1;
    font-size: 14px;
`;

const LayoutInfo = () => {
    const renderLayoutTooltip = (props: any) => (
        <Tooltip className="tooltip-heatmap-sensors" {...props}>
            <div>
                <div className="tooltip-heatmap-sensors-title">Layout:</div>
                <div style={{ display: "flex" }}>
                    {/* Set width and height to render before the image is loaded */}
                    <img src={Layout1} alt="Layout 1" />
                </div>
            </div>
        </Tooltip>
    );

    return (
        <LayoutWrapper>
            <span>Layout: </span>
            <OverlayTrigger
                placement="bottom"
                delay={{ show: 100, hide: 100 }}
                overlay={renderLayoutTooltip}
            >
                <span style={{ color: "#00CBAF" }}>See Layout</span>
            </OverlayTrigger>
        </LayoutWrapper>
    );
};

const defaultBoothRange = {
    minX: -2,
    minY: -2,
    maxX: 2,
    maxY: 2,
};

type TProps = {
    panel: any;
    dashboardId: string;
    isVisible?: boolean;
    visibleRef?: React.RefObject<any>;
    gatewaysConfig: any;
    onDelete: Function;
};

function HeatmapChart(props: Readonly<TProps>) {
    const chartRef = useRef<ReactECharts>(null);
    const longDateTimeFormat = useDateTimeFormatStore(
        (state) => state.longDateTimeFormat
    );
    const sessionRef = useRef<string | null>(null);

    const {
        panel: {
            attributes,
            uuid: panelId,
            dashboard_uuid,
            image_uuid,
            name: chartName,
        },
        gatewaysConfig,
        isVisible,
        visibleRef,
    } = props;

    const { boxWidth } = useChartWidth({
        ref: visibleRef,
        occupiedWidth: 14,
    });

    const sensorNames = (attributes || [])
        .map((attr: any) => {
            const gatewayConfig = _.get(gatewaysConfig, attr.gateway_id);
            const sensorName = _.get(gatewayConfig, "[0].sensor_names[0]");
            return sensorName;
        })
        .filter(Boolean);

    const [movementData, setMovementData] = useState<any>({
        peopleCount: 0,
    });
    const [chartOptions, setChartOptions] = useState<any>({});
    const [boothRange, setBoothRange] = useState(defaultBoothRange);
    const [chartBackground, setChartBackground] = useState<any>({});
    const boothWidth = boothRange.maxX - boothRange.minX;
    const boothHeight = boothRange.maxY - boothRange.minY;

    // Maximum dimension in pixels, more pixels will make the heatmap more smooth but affect performance
    const maxWidth = 150;
    const maxHeight = 150;
    const renderRate = 1000; // Milliseconds

    const { width: mapWidth, height: mapHeight } = calculateDimensions(
        maxWidth,
        maxHeight,
        boothWidth / boothHeight
    );
    const sigma = (mapWidth / 100) * 8;
    const targetWidth = sigma * 6.875;

    const handleWebsocketData = useCallback(
        (messageData: any[]) => {
            const data = transformSensorMessage(messageData);
            if (!data) {
                return;
            }

            const { range: deviceRange, timestamp } = data;

            if (
                deviceRange.minX !== boothRange.minX ||
                deviceRange.minY !== boothRange.minY ||
                deviceRange.maxX !== boothRange.maxX ||
                deviceRange.maxY !== boothRange.maxY
            ) {
                return setBoothRange({
                    minX: deviceRange.minX,
                    minY: deviceRange.minY,
                    maxX: deviceRange.maxX,
                    maxY: deviceRange.maxY,
                });
            }

            const deviceWidthRange = deviceRange.maxX - deviceRange.minX;
            const deviceHeightRange = deviceRange.maxY - deviceRange.minY;
            const humanData = new Map();
            const coordinates: any[] = [];

            const targets = Object.values(data.targets).flat();
            let peopleCount = targets.length;

            targets.forEach((target: any) => {
                const viewX = Math.round(
                    ((-data.range.minX + target.x) * mapWidth) /
                    deviceWidthRange
                );
                const viewY = Math.round(
                    ((-data.range.minY + target.y) * mapHeight) /
                    deviceHeightRange
                );

                // Remove people outside the booth
                if (
                    target.x < boothRange.minX ||
                    target.x > boothRange.maxX ||
                    target.y < boothRange.minY ||
                    target.y > boothRange.maxY
                ) {
                    peopleCount--;
                } else {
                    const heatObjectMaxWidth = Math.round(targetWidth);
                    coordinates.push([viewX, viewY]);

                    appendIntensityToHumanData(
                        humanData,
                        heatObjectMaxWidth,
                        viewX,
                        viewY,
                        sigma,
                        target.intensity
                    );
                }
            });

            const resultData = Array.from(humanData.entries()).map(
                ([key, intensity]) => {
                    const [posX, posY] = key.split(",").map(Number);
                    return [posX, posY, intensity];
                }
            );

            setMovementData({
                data: resultData,
                targets: coordinates,
                timestamp,
                peopleCount,
            });
        },
        [boothRange, mapWidth, mapHeight, targetWidth, sigma]
    );

    useEffect(() => {
        const fetchChartBackground = () => {
            dashboardService
                .getPanelImage(dashboard_uuid, panelId)
                .then(async (res) => {
                    if (isHttpSuccess(res.status)) {
                        const { data } = res;
                        const base64String = await fileToUri(data);
                        setChartBackground({
                            data: base64String,
                        });
                    } else {
                        setChartBackground(null);
                    }
                });
        };

        if (image_uuid) {
            fetchChartBackground();
        } else {
            setChartBackground(null);
        }
    }, [dashboard_uuid, panelId, image_uuid]);

    useEffect(() => {
        const initHeatmap = () => {
            if (!chartRef.current?.getEchartsInstance()) {
                return;
            }
            const padding = 20;
            const marginTop = 135;
            const widthOffset = 186; // other chart occupiedWidth is 200 and heatmap is 14. offset = 200 - 14.
            const bootRatio = boothWidth / boothHeight;
            const gridMaxWidth = boxWidth - padding * 2;
            const gridMaxHeight =
                chartRef.current.getEchartsInstance().getHeight() -
                marginTop -
                padding;

            const { width: chartGridWidth, height: chartGridHeight } =
                calculateDimensions(gridMaxWidth, gridMaxHeight, bootRatio);

            const left = (gridMaxWidth - chartGridWidth) / 2 + padding;
            const leftPercentage = (left / gridMaxWidth) * 100;

            const options: any = {
                grid: {
                    show: true,
                    left: `${leftPercentage}%`,
                    top: marginTop,
                    width: chartGridWidth,
                    height: chartGridHeight,
                    borderWidth: 0,
                    backgroundColor: "rgba(124, 179, 66, 0.1)",
                },
                xAxis: {
                    show: false,
                    type: "category",
                    data: Array.from({ length: mapWidth }, (_, i) => i),
                },
                yAxis: {
                    show: false,
                    type: "category",
                    data: Array.from({ length: mapHeight }, (_, i) => i),
                },
                title: {
                    textStyle: {
                        overflow: "truncate",
                        ellipsis: "...",
                        width: boxWidth - widthOffset,
                    },
                },
            };

            if (chartBackground?.data) {
                options.graphic = {
                    type: "image",
                    left: `${leftPercentage}%`,
                    top: marginTop,
                    z: -1, // Place the image behind other elements
                    style: {
                        image: chartBackground.data,
                        width: chartGridWidth,
                        height: chartGridHeight,
                        opacity: 0.8,
                    },
                };
            } else if (chartBackground === null) {
                const lowestColor = "#0d47a1";
                options.grid.backgroundColor = lowestColor;
                options.visualMap = {
                    show: false,
                    min: 0,
                    max: 100, // Color intensity range
                    inRange: {
                        color: [
                            lowestColor,
                            "#43a047",
                            "#ffeb3b",
                            "#ffa726",
                            "#f4511e",
                        ],
                    },
                };
            }

            chartRef.current?.getEchartsInstance().setOption(options);
        };

        if (chartRef.current) {
            initHeatmap();
        }
    }, [
        boothHeight,
        boothWidth,
        boxWidth,
        mapWidth,
        mapHeight,
        chartBackground,
    ]);

    useEffect(() => {
        const option: EChartsOption = {
            ...cloneDeep(DEFAULT_OPTION),
            title: {
                ...DEFAULT_OPTION.title,
                text: chartName || "PCS Heatmap",
                subtext: `Heatmap of people\n{s6|0}\n`,
                subtextStyle: {
                    ...DEFAULT_OPTION.title?.subtextStyle,
                    rich: {
                        ...DEFAULT_OPTION.title?.subtextStyle?.rich,
                        s6: {
                            color: "#65ACEF",
                            fontSize: 30,
                            fontWeight: 500,
                            fontFamily: "Roboto",
                            padding: [10, 0, 5, 0],
                        },
                    },
                },
            },
            toolbox: {
                ...DEFAULT_OPTION.toolbox,
                feature: {
                    saveAsImage: DEFAULT_OPTION.toolbox?.feature?.saveAsImage,
                },
            },
            dataZoom: undefined,
            tooltip: {
                show: false,
                position: "top",
            },
            grid: {},
            visualMap: {
                show: false,
                min: 0,
                max: 100, // Color intensity range
                inRange: {
                    color: [
                        "rgba(124, 179, 66, 0.01)",
                        "rgba(124, 179, 66, 0.05)",
                        "rgba(124, 179, 66, 0.85)",
                        "rgba(255, 235, 59, 0.9)",
                        "rgba(255, 167, 38, 0.95)",
                        "rgba(244, 81, 30)",
                    ],
                },
            },
        };

        setChartOptions(option);
    }, [chartName]);

    useEffect(() => {
        if (!isVisible || !attributes || !handleWebsocketData) {
            return;
        }

        const gatewayIds = (attributes || [])
            .map((attr: any) => attr.gateway_id)
            .filter(Boolean);
        if (gatewayIds.length === 0) {
            return;
        }

        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let isDisposed = false;
        let nextDelay = renderRate;

        const fetchData = async () => {
            if (isDisposed) {
                return;
            }

            try {
                if (!WS.isReady()) {
                    await WS.initConnection();
                }

                if (isDisposed) {
                    return;
                }

                if (!sessionRef.current) {
                    sessionRef.current = getRandomString();
                }

                const responses = await WS.fetchData(
                    sessionRef.current,
                    gatewayIds
                );

                if (isDisposed) {
                    return;
                }

                handleWebsocketData(responses);
                nextDelay = renderRate;
            } catch (error: any) {
                // Back off when backend reports update rate limiting.
                const isRateLimited =
                    error?.error?.update === "too frequently" ||
                    error?.message?.includes?.("too frequently");
                nextDelay = isRateLimited ? renderRate * 2 : renderRate;
            } finally {
                // Schedule next fetch after current one completes.
                if (!isDisposed) {
                    timeoutId = setTimeout(fetchData, nextDelay);
                }
            }
        };

        fetchData();

        return () => {
            isDisposed = true;
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }

            if (sessionRef.current) {
                WS.closeConnection(sessionRef.current);
                sessionRef.current = null;
            }
        };
    }, [handleWebsocketData, attributes, isVisible]);

    useEffect(() => {
        if (!chartRef?.current || !movementData.data) {
            return;
        }

        const displayTime = movementData.timestamp
            ? moment(movementData.timestamp).format(longDateTimeFormat)
            : "";

        const targets = new Set(
            movementData.targets.map(
                (target: any) => `${target[0]}-${target[1]}`
            )
        );

        const heatmapData = movementData.data.map((item: any) => {
            const coordinate = `${item[0]}-${item[1]}`;
            let itemStyle;
            const defaultStyle = {
                normal: {
                    color: "#000",
                    borderWidth: 0,
                    shadowBlur: 0,
                },
            };

            if (
                targets.has(coordinate) ||
                targets.has(`${item[0] - 1}-${item[1]}`) ||
                targets.has(`${item[0] + 1}-${item[1]}`) ||
                targets.has(`${item[0]}-${item[1] - 1}`) ||
                targets.has(`${item[0]}-${item[1] + 1}`)
            ) {
                itemStyle = defaultStyle;
            }

            return {
                value: item,
                itemStyle,
            };
        });

        const newOption = {
            title: {
                ...chartOptions.title,
                subtext: `Heatmap of people\n{s6|${movementData.peopleCount || 0
                    }}\n${displayTime}`,
            },
            series: [
                {
                    name: "Movements",
                    type: "heatmap",
                    data: heatmapData,
                    progressive: 800000,
                    animation: false,
                },
            ],
        };

        chartRef.current
            .getEchartsInstance()
            .setOption(newOption, { notMerge: false, lazyUpdate: true });
    }, [movementData, chartOptions.title, longDateTimeFormat]);

    return (
        <ChartBox className="widget-box heatmap-box w-100" ref={visibleRef}>
            <div className="widget">
                {attributes.length > 1 && (
                    <>
                        <SensorInfo sensorNames={sensorNames} />
                        <LayoutInfo />
                    </>
                )}

                {attributes.length === 1 && (
                    <SingleSensorInfo sensorName={_.get(sensorNames, "[0]")} />
                )}

                <ContentWrapper>
                    <ReactECharts
                        ref={chartRef}
                        option={chartOptions}
                        className="chart-box"
                        theme={"dark"}
                        notMerge={true}
                        lazyUpdate={true}
                        style={{ height: "100%", width: "100%" }}
                    />
                </ContentWrapper>
            </div>
        </ChartBox>
    );
}

export default memo(HeatmapChart);
